import { getKiloWeight } from 'api/lib/postBookingBaggageMap'

type ReservationAddon = NonNullable<App.FlightOrderItemDetails['reservationAddons']>[number]

/**
 * How a purchased bag reads on the journey.
 *
 * svc-flights already drops bags it could not deliver, so anything that arrives here is a bag the
 * customer paid for. It is still worth mapping the status explicitly rather than defaulting: a bag
 * is provider-booked asynchronously, so `created`/`pending` is the normal state for the first few
 * seconds after payment, and only `confirmed` may be presented as done.
 */
function getBaggageAddonStatus(status: string): App.LegAddon['status'] | undefined {
  if (status === 'confirmed') { return 'confirmed' }
  if (status === 'created' || status === 'pending') { return 'pending' }
  return undefined
}

/**
 * A bag bought after booking is never written into `booking_journey.legs[].addons` — that array is
 * built once, at reservation creation, and frozen. The purchase lives on as a `reservation_addons`
 * row instead, which svc-flights returns alongside the journey.
 *
 * This turns those rows back into the `App.LegAddon` shape the journey already speaks, so every
 * existing reader — the My Escapes traveller card, the order/confirmation journey panel, the
 * "already has a bag" guard on `Add bags` — picks the purchase up without knowing it came from a
 * different place.
 */
export function buildLegBaggageAddons(
  legId: string | undefined,
  reservationAddons: Array<ReservationAddon> | undefined,
): Array<App.LegAddon> {
  if (!legId || !reservationAddons?.length) {
    return []
  }

  const addons: Array<App.LegAddon> = []

  for (const addon of reservationAddons) {
    // Only the post-booking write path records `legId`. Baggage bought at booking time also has a
    // `reservation_addons` row, but it is already in `leg.addons`, so mapping it here as well would
    // show every pre-paid bag twice.
    if (addon.addon_type !== 'BAGGAGE' || addon.legId !== legId || !addon.travellerId) { continue }

    const status = getBaggageAddonStatus(addon.status)
    if (!status) { continue }

    const kilos = getKiloWeight(addon.allowance)

    addons.push({
      type: 'checked_baggage',
      travellerId: addon.travellerId,
      quantity: addon.quantity ?? 1,
      amount: addon.price,
      currency: addon.currency,
      description: addon.name ?? addon.productCode ?? '',
      status,
      // Restated in the journey's own units so the weight label is derived identically for a bag
      // bought at booking and one bought after it. `limitType: 'U'` because `getKiloWeight` only
      // ever returns a weight the bag actually buys, never an overage threshold.
      ...(kilos !== undefined && {
        weightLimits: [{ weight: kilos, unit: 'KILO' as const, limitType: 'U' as const }],
      }),
    })
  }

  return addons
}
